import Button from "@/components/Button"
import { ButtonGroup } from "@/components/chadcn/ButtonGroup"
import DatePicker from "@/components/DatePicker"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import {
  usePatientDetailPageDataQuery,
  usePatientReadingsQuery,
} from "@/data/queries"
import { LoginState } from "@/model/Patient"
import LabeledText from "@/pages/patient-details/LabeledText"
import EMRNotificationsCalendar from "@/pages/patient-readings/EMRNotificationsCalendar"
import EMRNotificationsTable from "@/pages/patient-readings/EMRNotificationsTable"
import ReadingsTable from "@/pages/patient-readings/ReadingsTable"
import { buildPath, ROUTES } from "@/routes"
import cn from "@/utils/cn"
import { DateTimeFormatter, Instant, LocalDate, ZoneId } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"

type ReadingsViewMode = "table" | "calendar" | "split"

const PatientReadingsPage = () => {
  const navigate = useNavigate()
  const { bloomlifeId, prescriptionId } = useParams<{
    bloomlifeId: string
    prescriptionId: string
  }>()

  const {
    data: patientData,
    isPending: isLoadingPatientData,
    isError: isErrorPatientData,
  } = usePatientDetailPageDataQuery(bloomlifeId ?? "")

  const {
    data: readingsData,
    isPending: isLoadingReadingsData,
    isError: isErrorReadingsData,
  } = usePatientReadingsQuery(bloomlifeId ?? "", prescriptionId ?? "")

  const today = LocalDate.now()
  const [selectedMonth, setSelectedMonth] = useState<{
    year: number
    month: number
  }>({
    year: today.year(),
    month: today.monthValue(),
  })

  const [readingsViewMode, setReadingsViewMode] =
    useState<ReadingsViewMode>("table")

  const [highlightedNotificationId, setHighlightedNotificationId] = useState<
    string | null
  >(null)
  const [highlightedCellDate, setHighlightedCellDate] = useState<string | null>(
    null
  )

  useEffect(() => {
    if (highlightedNotificationId) {
      const timeout = setTimeout(() => {
        setHighlightedNotificationId(null)
      }, 2500)
      return () => clearTimeout(timeout)
    }
  }, [highlightedNotificationId])

  useEffect(() => {
    if (highlightedCellDate) {
      const timeout = setTimeout(() => {
        setHighlightedCellDate(null)
      }, 2500)
      return () => clearTimeout(timeout)
    }
  }, [highlightedCellDate])

  if (!bloomlifeId || !prescriptionId) {
    return <div>Invalid parameters</div>
  }

  // TODO: This should ACTUALLY go back, instead of navigating to the patient detail page
  // Maybe fix this in the SidebarLayout component
  const goBack = () =>
    navigate(
      buildPath(ROUTES.POPULATION.PATIENT_DETAIL, { bloomlifeId: bloomlifeId })
    )

  const handleNavigateToRow = (notificationId: string) => {
    setHighlightedNotificationId(notificationId)
    setTimeout(() => {
      const element = document.getElementById(
        `notification-row-${notificationId}`
      )
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }, 100)
  }

  const handleNavigateToCell = (timestamp: Instant) => {
    const date = timestamp
      .atZone(ZoneId.of(organization.timeZone))
      .toLocalDate()
    const dateKey = date.toString()

    setSelectedMonth({
      year: date.year(),
      month: date.monthValue(),
    })

    setHighlightedCellDate(dateKey)
    setTimeout(() => {
      const element = document.getElementById(`calendar-cell-${dateKey}`)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }, 200)
  }

  if (isLoadingPatientData || isLoadingReadingsData) {
    return (
      <Page>
        <Spinner className="mt-4" />
      </Page>
    )
  }

  if (isErrorPatientData || isErrorReadingsData) {
    return (
      <Page>
        <p className="mt-4 text-center italic text-red-500">
          Something went wrong while loading the data, please try again.
        </p>
      </Page>
    )
  }

  if (!patientData || !readingsData) {
    return (
      <Page>
        <Spinner className="mt-4" />
      </Page>
    )
  }

  const {
    prescription,
    firstMeasurementTime,
    totalMeasurements,
    totalContextualizations,
    readings,
    emrNotifications,
  } = readingsData

  const patient = patientData.patient
  const organization = patientData.organization
  const medicalRecordNumber = patient.medicalRecordNumber

  const breadcrumbs = [
    { element: "Patient overview", href: "/" },
    { element: organization.longName ?? "-" },
    { element: medicalRecordNumber },
    { element: "Readings" },
  ]

  const prescriptionTypeTitle =
    prescription.type === "diabetes" ? "Blood Glucose" : "Blood Pressure"

  const formattedFirstMeasurement = firstMeasurementTime
    ? firstMeasurementTime
        .atZone(ZoneId.of(patient.timeZone))
        .format(
          DateTimeFormatter.ofPattern("MM/dd/yyyy hh:mm a").withLocale(
            Locale.US
          )
        )
    : "-"

  return (
    <Page breadcrumbs={breadcrumbs}>
      <div className="mx-auto mb-6 w-full max-w-[1100px]">
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span
              className="material-icons cursor-pointer rounded-md bg-primary/10 p-1 text-[28px] transition-colors hover:bg-primary/20"
              onClick={goBack}
            >
              keyboard_arrow_left
            </span>
            <p className="text-4xl font-bold">
              {patient.lastName}, {patient.firstName}
            </p>
            <span
              className={cn(
                "mt-1 rounded-full px-4 py-1 text-sm font-bold tracking-wide text-primary",
                classForLoginStateCapsule(patient.loginState)
              )}
            >
              {titleForLoginState(patient.loginState)}
            </span>
          </div>
        </div>

        <div className="my-6">
          <h4 className="mb-3 text-2xl font-extrabold">
            {prescriptionTypeTitle} Readings
          </h4>
          <div className="rounded-xl border border-gray-300 p-4">
            <div className="grid grid-cols-3 gap-4">
              <LabeledText
                label="First measurement"
                value={formattedFirstMeasurement}
                copy={false}
              />
              <LabeledText
                label="Total measurements"
                value={totalMeasurements}
                copy={false}
              />
              {prescription.type === "diabetes" && (
                <LabeledText
                  label="Total contextualizations"
                  value={totalContextualizations}
                  copy={false}
                />
              )}
            </div>
          </div>
        </div>

        <div className="my-6">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-2xl font-extrabold">
              Readings
              <span className="ml-2 text-base font-normal text-gray-500">
                ({totalMeasurements} total)
              </span>
            </h4>
            <ButtonGroup>
              <Button
                variant={readingsViewMode === "table" ? "primary" : "neutral"}
                onClick={() => setReadingsViewMode("table")}
                className="px-2 py-1"
                aria-label="Table view"
              >
                <span className="material-icons !text-sm">table_chart</span>
              </Button>
              <Button
                variant={
                  readingsViewMode === "calendar" ? "primary" : "neutral"
                }
                onClick={() => setReadingsViewMode("calendar")}
                className="px-2 py-1"
                aria-label="Calendar view"
              >
                <span className="material-icons !text-sm">calendar_month</span>
              </Button>
              <Button
                variant={readingsViewMode === "split" ? "primary" : "neutral"}
                onClick={() => setReadingsViewMode("split")}
                className="px-2 py-1"
                aria-label="Split screen view"
              >
                <span className="material-icons !text-sm">vertical_split</span>
              </Button>
            </ButtonGroup>
          </div>
          {readingsViewMode === "table" && (
            <div className="rounded-xl border border-gray-300">
              <ReadingsTable
                readings={readings}
                prescriptionType={prescription.type}
                patientTimeZone={patient.timeZone}
              />
            </div>
          )}
          {readingsViewMode === "calendar" && (
            <div className="rounded-xl border border-gray-300 p-8 text-center italic text-gray-500">
              Calendar view (to be developed)
            </div>
          )}
          {readingsViewMode === "split" && (
            <div className="rounded-xl border border-gray-300 p-8 text-center italic text-gray-500">
              Split screen view (to be developed)
            </div>
          )}
        </div>

        <div className="my-6">
          <h4 className="mb-3 text-2xl font-extrabold">EMR Notifications</h4>
          <div className="mb-4">
            <DatePicker
              selectedMonth={selectedMonth}
              onMonthChange={(year, month) => setSelectedMonth({ year, month })}
            />
          </div>

          <div className="mb-6 rounded-xl border border-gray-300 p-4">
            <EMRNotificationsCalendar
              notifications={emrNotifications}
              clinicTimeZone={organization.timeZone}
              selectedMonth={selectedMonth}
              highlightedCellDate={highlightedCellDate}
              onNavigateToRow={handleNavigateToRow}
            />
          </div>

          <div className="rounded-xl border border-gray-300">
            <EMRNotificationsTable
              notifications={emrNotifications}
              clinicTimeZone={organization.timeZone}
              highlightedId={highlightedNotificationId}
              onNavigateToCell={handleNavigateToCell}
            />
          </div>
        </div>
      </div>
    </Page>
  )
}

export default PatientReadingsPage

const titleForLoginState = (loginState: LoginState) => {
  switch (loginState) {
    case "no-access":
      return "Disabled"
    case "invited":
      return "Invited"
    case "active":
      return "Active"
  }
}

const classForLoginStateCapsule = (loginState: LoginState) => {
  switch (loginState) {
    case "no-access":
      return "bg-gray-200 text-primary/50"
    case "invited":
      return "bg-yellow-200 text-yellow-700"
    case "active":
      return "bg-green-200 text-green-700"
  }
}
